import 'twin.macro';
import React, { useEffect, useRef } from 'react';
import { Disclosure as HLDisclosure } from '@headlessui/react';
import styled from '@emotion/styled';
import { BaseButtonProps } from '@teetsh/app/src/components/Buttons/BaseButtonProps';
import { buttonRootStyle } from '@teetsh/app/src/components/Buttons/Button';
import { ButtonContent } from '@teetsh/app/src/components/Buttons/ButtonContent';
import clsx from 'clsx';
import { ChevronUpIcon as ChevronUp } from '@heroicons/react/24/outline';
import useDisclosureContext from '@teetsh/app/src/components/Disclosure/useDisclosureContext';
import tw from 'twin.macro';

type IconRotation = 'right-to-bottom' | 'top-to-bottom' | 'bottom-to-top';

interface Props {
  children?: React.ReactNode;
  withoutChevrons?: boolean;
  isOpen?: boolean;
  controlled?: boolean;
  noButton?: boolean;
  iconPosition?: 'left' | 'right';
  icon?: any;
  iconRotation?: IconRotation;
  'aria-label'?: string;
}

interface IconStyleProps {
  iconRotation?: IconRotation;
  open?: boolean;
  disabled?: boolean;
}

function getIconStyle({ disabled, iconRotation, open }: IconStyleProps) {
  if (disabled) {
    return {};
  }
  if (iconRotation === 'right-to-bottom') {
    return open
      ? tw`transition duration-200 transform rotate-180`
      : tw`transition duration-200 transform rotate-90`;
  }
  if (iconRotation === 'top-to-bottom') {
    return open
      ? tw`transition duration-200 transform rotate-180`
      : tw`transition duration-200 transform`;
  }
  if (iconRotation === 'bottom-to-top') {
    return open
      ? tw`transition duration-200 transform rotate-0`
      : tw`transition duration-200 transform rotate-180`;
  }
  return;
}

const StyledDisclosureButton: any = styled(
  ({
    buttonRef,
    className,
    children,
    open,
    withoutChevrons,
    iconPosition,
    iconRotation,
    icon,
    ...rest
  }) => (
    <HLDisclosure.Button
      ref={buttonRef}
      className={clsx('tsh-button', className)}
      aria-label={rest['aria-label']}
      tw="appearance-none"
    >
      <ButtonContent
        {...rest}
        iconStyle={getIconStyle({
          disabled: rest.disabled || iconPosition === 'right',
          iconRotation,
          open,
        })}
        icon={iconPosition === 'left' ? ChevronUp : icon}
        rightIconStyle={getIconStyle({
          disabled: rest.disabled,
          iconRotation,
          open,
        })}
        rightIcon={iconPosition === 'right' ? ChevronUp : null}
      >
        {children}
      </ButtonContent>
    </HLDisclosure.Button>
  ),
)((p: BaseButtonProps) => [buttonRootStyle(p)]);

export default function DisclosureButton(props: Props & BaseButtonProps) {
  const { isOpen, controlled, noButton } = props;
  // si tu mets div à la place de Fragment tu as safari qui te style le wrapper ...
  const { open } = useDisclosureContext();
  const buttonRef = useRef();
  const openState = useRef(isOpen);
  // C'est parce que le disclosure headless ui n'est pas controllable
  useEffect(() => {
    if (controlled && openState.current !== isOpen) {
      (buttonRef.current as any).click();
      openState.current = isOpen;
      return;
    }
    if (isOpen && buttonRef?.current) {
      (buttonRef.current as any).click();
    }
  }, [isOpen]);

  if (noButton) {
    return (
      <HLDisclosure.Button
        ref={buttonRef as any}
        tw="appearance-none"
        className={props.className}
        aria-label={props['aria-label']}
      >
        <div
          role="presentation"
          onClick={e => {
            if (controlled) {
              e.stopPropagation();
            }
          }}
        >
          {props.children}
        </div>
      </HLDisclosure.Button>
    );
  }
  return (
    <StyledDisclosureButton
      {...props}
      iconRotation={props.iconRotation || 'top-to-bottom'}
      hasChildren={!!props.children}
      iconPosition={props.iconPosition || 'left'}
      withoutChevrons={!!props.withoutChevrons}
      open={open}
      buttonRef={buttonRef}
    />
  );
}
